import { isProcurementLockedForEdits } from "@boark/shared/types/procurement";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Modal } from "../../components/modal";
import {
  ProcurementForm,
  ProcurementFormValues,
} from "../../components/procurement-form";
import { useGetProcurement } from "../../queries/procurement";
import {
  procurementFormValuesToUpdate,
  updateProcurement,
} from "../../services/procurement";
import { navigateBack } from "../../utils/navigate-back";
import { recordIdToId } from "../../utils/record-id-to-id";

export function ProcurementEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Procurement ID is required");
  }

  const { data: procurement, isLoading: isLoadingProcurement } =
    useGetProcurement(id);
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<ProcurementFormValues>({
    title: "",
    description: "",
    tags: [],
    organizations: [],
  });

  const isLoading = isLoadingProcurement || loading;

  useEffect(() => {
    if (procurement && isProcurementLockedForEdits(procurement.state)) {
      navigateBack(navigate, `/admin/procurement/${id}`);
      return;
    }

    if (procurement) {
      setFormValues({
        title: procurement.title,
        description: procurement.description ?? "",
        tags: [...procurement.tags],
        organizations: procurement.organizations.map((organization) => ({
          organizationId: recordIdToId(organization.organization.id),
          price:
            organization.price !== undefined ? String(organization.price) : "",
        })),
      });
    }
  }, [id, navigate, procurement]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/procurement/list");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <ProcurementForm
        hasError={hasError}
        loading={isLoading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await updateProcurement(
                id,
                procurementFormValuesToUpdate(formValues),
              );
              await Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["procurement", id],
                }),
                queryClient.invalidateQueries({ queryKey: ["procurements"] }),
              ]);
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
